import { useMutation, useQuery } from "@tanstack/react-query";

import { copilotApi } from "src/api/endpoints";
import {
  ConversationMessageRoleEnum,
  ConverseRequest,
  ConverseResponse,
  NodeConversationContext,
} from "src/clients/copilot";
import { queryClient } from "src/queryClient";
import { logger } from "src/utils/logger";
import { errorMessage } from "src/utils/stringUtils";
import { PartialBy } from "src/utils/typing";

const converseKey = (conversationId: string) => ["converse", conversationId];
const conversationKey = (conversationId: string) => [
  "conversation",
  conversationId,
];

export const useConverse = (args: {
  conversationId: string;
  baseUrl: string;
}) => {
  return useMutation({
    mutationKey: converseKey(args.conversationId),
    mutationFn: async (
      request: PartialBy<ConverseRequest, "conversation_id">,
    ) => {
      const converseRequest = {
        conversation_id: args.conversationId,
        ...request,
      };
      const response = await copilotApi(
        args.baseUrl,
      ).converseCopilotApiV1ConversePost({
        converseRequest,
      });
      if (response.data) {
        return response.data;
      } else {
        throw new Error(response.statusText);
      }
    },
  });
};

export const useConversation = (args: { conversationId: string }) => {
  return useQuery<ConverseResponse | null>({
    queryKey: conversationKey(args.conversationId),
    queryFn: () => null,
    enabled: false,
  });
};

export const useAskCopilot = (args: {
  conversationId: string;
  baseUrl: string;
}) => {
  const { mutateAsync } = useConverse(args);

  return useMutation({
    mutationKey: conversationKey(args.conversationId),
    mutationFn: async (params: {
      message: string;
      context: NodeConversationContext;
    }) => {
      // add message to conversation
      const queryKey = conversationKey(args.conversationId);
      const prevConvo = queryClient.getQueryData<ConverseResponse | undefined>(
        queryKey,
      ) ?? { messages: [], completion_id: "", conversation_id: "" };

      const newMessages = [
        ...prevConvo.messages,
        {
          role: ConversationMessageRoleEnum.USER,
          content: params.message,
        },
      ];

      // update query data immediately
      queryClient.setQueryData<ConverseResponse>(queryKey, {
        ...prevConvo,
        messages: newMessages,
      });

      const converseRequest = {
        conversation_id: args.conversationId,
        context: params.context,
        messages: newMessages,
      };
      try {
        const response = await mutateAsync(converseRequest);

        // update conversation
        queryClient.setQueryData<ConverseResponse>(queryKey, {
          ...response,
          messages: [...newMessages, ...response.messages],
        });

        return true;
      } catch (e) {
        logger.error(e);
        queryClient.setQueryData<ConverseResponse>(queryKey, {
          ...prevConvo,
          messages: [
            ...newMessages,
            {
              role: ConversationMessageRoleEnum.COPILOT,
              content: `Error: ${errorMessage(e)}`,
            },
          ],
        });
        return false;
      }
    },
  });
};
